import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useAuthValidate } from '@hooks/security/use-auth-validate';
import { getPostLoginUrl } from '@/security/session';
import { useLocalize } from '@hooks/use-localize';
import { authValidateSchema } from '@views/security/auth-validate.schema';
import type { IAuthValidateFormValues, IAuthValidateModel, IAuthValidateSubmitResult } from '@/types/security/auth-validate';
import FieldError from '@components/field-error';

const AuthValidate = () => {
    const { token } = useParams();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useAuthValidate(token ?? '');

    if (isLoading) {
        return (
            <div className="row">
                <div className="col-xs-12">Loading&hellip;</div>
            </div>
        );
    }

    return (
        <>
            {error && (
                <div className="row">
                    <div className="col-xs-12">
                        <div className="alert alert-danger">{error}</div>
                    </div>
                </div>
            )}
            <AuthValidateContent
                model={model}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submit}
            />
        </>
    );
};

interface IAuthValidateContentProps {
    model: IAuthValidateModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: (values: IAuthValidateFormValues) => Promise<IAuthValidateSubmitResult>;
}

const AuthValidateContent = ({ model, isSubmitting, submitError, onSubmit }: IAuthValidateContentProps) => {
    const t = useLocalize(model.languageData);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const {
        register,
        handleSubmit,
        setError,
        formState: { errors },
    } = useForm<IAuthValidateFormValues>({
        resolver: zodResolver(authValidateSchema),
        defaultValues: { code: '' },
    });

    const onValid = async (values: IAuthValidateFormValues) => {
        const result = await onSubmit(values);

        // Surface server-side field errors through the same channel as client validation.
        if (!result.ok && result.errors) {
            Object.entries(result.errors).forEach(([field, message]) => {
                setError(field as keyof IAuthValidateFormValues, { type: 'server', message });
            });
        }

        if (result.ok) {
            navigate(getPostLoginUrl(searchParams), { replace: true });
        }
    };

    return (
        <div className='container'>
            <div className="row">
                <div className="col-xs-12">
                    <div className="alert alert-info" style={{ fontSize: 16 }}>
                        <p style={{ marginBottom: 10 }}>
                            {t(
                                'DashLabel_WeHaveAttemptedToSendATextMessageWithAnAuthenticationCode',
                                'We have attempted to send a text message with an authentication code to your chosen destination (email or mobile phone).  Please enter the authentication code in the text box below to continue',
                            )}
                            .
                        </p>
                        <p style={{ marginBottom: 10 }}>
                            {t(
                                'DashLabel_IfYouDidNotReceiveATextMessageItMayBeForOneOfTheFollowing',
                                'If you did not receive a text message it may be for one of the following reasons',
                            )}
                            :
                        </p>
                        <ol style={{ paddingInlineStart: 15, marginBottom: 0 }}>
                            <li>{t('DashLabel_YouAreNotRegistered', 'You are not registered')}.</li>
                            <li>
                                {t(
                                    'DashLabel_YourEMailOrPhoneDidNotExactlyMatchYourRegistration',
                                    'Your e-mail or phone did not exactly match your registration',
                                )}
                                .
                            </li>
                        </ol>
                    </div>

                    {submitError && (
                        <div className="alert alert-danger">
                            {t(
                                'DashLabel_WeAreUnableToVerifyTheCodePleaseResolveAnyIssuesIndicated',
                                'We are unable to verify the code, please resolve any issues indicated below',
                            )}
                        </div>
                    )}

                    <form id="RegistrationForm" className="form" onSubmit={handleSubmit(onValid)}>
                        <div id="codeGroup" className="form-group">
                            <label htmlFor="Code">
                                <FieldError message={errors.code?.message} />{' '}
                                {t('DashLabel_AuthorizationCode', 'Authorization Code')}:
                            </label>
                            <input
                                id="Code"
                                type="number"
                                className="form-control"
                                maxLength={6}
                                {...register('code')}
                            />
                        </div>

                        <div style={{ marginTop: 16 }}>
                            <button
                                type="submit"
                                className="btn btn-primary btn-lg"
                                style={{ marginTop: 20 }}
                                disabled={isSubmitting}
                            >
                                {t('Word_Verify', 'Verify')}
                            </button>
                        </div>
                    </form>

                    <div className="col-xs-12 col-sm-3 col-sm-offset-1"></div>
                </div>
            </div>
        </div>
    );
};

export default AuthValidate;
